<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';

// A read-completed «حجوزاتي» row: one of the member's Poola claims, folded flat
// from the /poola-purchases card (offer identity + run label/date + qty + the
// cash/PoolShare split the member paid). No CTA, no progress, no share — a claim
// is history, not a buy surface.
const props = defineProps({
  // The deserialised poola-purchases resource (id + flattened attributes).
  claim: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { formatCurrency } = useCurrency();

const hasSupplier = computed(() => {
  const n = props.claim.supplierName;
  return n != null && String(n).trim() !== '';
});

// Western digits only (owner-locked): the `-u-nu-latn` unicode extension forces
// Latin numerals while keeping Arabic month names, so a claim date never renders
// Arabic-Indic. Falls back to '' on a missing/invalid date (the line hides).
const claimedOn = computed(() => {
  const raw = props.claim.createdAt;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d);
  } catch {
    return '';
  }
});

// «حجوزاتي» reward chip: shown ONLY when the delivered claim carries a real
// amount (poolcreditEarnedCents != null). Per the DELIVERED contract a completed
// claim with a null amount is a standalone / all-cash offer that NEVER granted, so
// it must show NO «بولشيرك رجعلك بولكريديت» line (brief, /poola-purchases). Gating
// on the amount — not offerCompleted — keeps us from claiming a reward the member
// never received. Reuses the offer card's approved «رجعلك {amount}» string.
const hasReward = computed(
  () => props.claim.offerCompleted && props.claim.poolcreditEarnedCents != null,
);
const rewardChipText = computed(() =>
  t('poola.buy.card.rewardChip', { amount: formatCurrency(props.claim.poolcreditEarnedCents) }),
);
</script>

<template>
  <f7-card class="poola-claim-card">
    <div class="claim-row">
      <!-- Thumbnail -->
      <div class="claim-thumb">
        <img
          v-if="claim.photoUrl"
          :src="claim.photoUrl"
          :alt="claim.displayTitle"
          class="claim-thumb__img"
          loading="lazy"
          @error="(e) => e.target.classList.add('claim-thumb__img--hidden')"
        />
        <div v-else class="claim-thumb__placeholder">
          <f7-icon ios="f7:bag_fill" md="material:shopping_bag" size="26" color="white"></f7-icon>
        </div>
      </div>

      <!-- Body -->
      <div class="claim-body">
        <h3 class="claim-title">{{ claim.displayTitle }}</h3>

        <p v-if="hasSupplier" class="claim-supplier">{{ claim.supplierName }}</p>

        <!-- Run label + claim date — the cross-run disambiguator (each claim can
             belong to a different Poola run). Either half hides when absent. -->
        <p v-if="claim.runLabel || claimedOn" class="claim-run">
          <span v-if="claim.runLabel">{{ claim.runLabel }}</span>
          <span v-if="claim.runLabel && claimedOn"> · </span>
          <span v-if="claimedOn">{{ claimedOn }}</span>
        </p>

        <div class="claim-meta">
          <span class="claim-qty">{{ t('poola.buy.mine.claimedBadge', { n: claim.qty }) }}</span>
          <!-- Reward chip — the member's spent PoolShare came back as poolcredit.
               Gated on the delivered per-claim amount (poolcreditEarnedCents;
               2026-07-13-poola-completion-poolcredit-DELIVERED.md), NOT on
               offerCompleted: a completed standalone / all-cash claim carries a null
               amount because it never granted, and must show no reward line. -->
          <span v-if="hasReward" class="claim-reward">{{ rewardChipText }}</span>
          <!-- dir="ltr" keeps cash-left / poolshare-right regardless of page
               direction — mirrors the offer card's split legend. -->
          <span class="claim-paid" dir="ltr">
            {{ t('poola.buy.card.cashLabel') }} {{ formatCurrency(claim.totalCashCents) }} ·
            {{ t('poola.buy.card.poolshareLabel') }} {{ formatCurrency(claim.totalPoolshareCents) }}
          </span>
        </div>
      </div>
    </div>
  </f7-card>
</template>

<style scoped>
.poola-claim-card {
  margin: var(--poolit-spacing-sm, 8px) var(--poolit-spacing-md, 16px);
  border-radius: var(--poolit-radius-lg, 16px);
  overflow: hidden;
}

.claim-row {
  display: flex;
  align-items: stretch;
  gap: var(--poolit-spacing-md, 12px);
  padding: var(--poolit-spacing-sm, 10px);
}

/* ── Thumbnail ────────────────────────────────────────────────────────────── */
.claim-thumb {
  position: relative;
  flex-shrink: 0;
  width: 76px;
  height: 76px;
  border-radius: var(--poolit-radius-md, 10px);
  overflow: hidden;
  background: var(--poolit-primary-900, #3b0764);
}

.claim-thumb__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.claim-thumb__img--hidden {
  display: none !important;
}

.claim-thumb__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--poolit-primary-700, #6d28d9),
    var(--poolit-primary-900, #3b0764)
  );
}

/* ── Body ─────────────────────────────────────────────────────────────────── */
.claim-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.claim-title {
  font-size: var(--poolit-font-size-body, 0.9375rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  margin: 0;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.claim-supplier {
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--f7-text-color-secondary, #6b7280);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.claim-run {
  font-size: 12px;
  color: var(--f7-text-color-secondary, #6b7280);
  margin: 0;
}

.claim-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-top: 4px;
}

.claim-qty {
  display: inline-block;
  padding: 3px 10px;
  background: var(--poolit-primary-100);
  color: var(--poolit-primary-700);
  border-radius: var(--poolit-radius-full);
  font-size: var(--poolit-font-size-small, 0.8125rem);
  font-weight: var(--poolit-font-weight-semibold);
  white-space: nowrap;
}

.claim-paid {
  font-size: 12px;
  color: var(--f7-text-color-secondary, #6b7280);
}

/* Gold reward tier — reuses the same coin-face tokens as the PoolShare rate
   badge's gold tier (see _branding-variables.css), the established "top
   reward" gold across the app. */
.claim-reward {
  display: inline-block;
  padding: 3px 10px;
  background: var(--poolshare-rate-gold-bg);
  color: var(--poolshare-rate-gold-fg);
  border-radius: var(--poolit-radius-full);
  font-size: var(--poolit-font-size-small, 0.8125rem);
  font-weight: var(--poolit-font-weight-semibold);
  white-space: nowrap;
}
</style>
